import { CheckCircle2, Clock, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { StructuredProfile } from '@/features/charter/redux/types/charterTypes'

interface PrepProfileCardProps {
  profile: StructuredProfile
  sharingMode: string
  profileApproved: boolean
  onClick: () => void
}

const SHARING_MODE_LABELS: Record<string, string> = {
  auto: 'Auto',
  selective: 'Selective',
  manual: 'Private',
}

const SHARING_MODE_COLORS: Record<string, string> = {
  auto: 'bg-blue-50 text-blue-700 border-blue-200',
  selective: 'bg-amber-50 text-amber-700 border-amber-200',
  manual: 'bg-gray-50 text-gray-600 border-gray-200',
}

export function PrepProfileCard({
  profile,
  sharingMode,
  profileApproved,
  onClick,
}: PrepProfileCardProps) {
  const sectionCount =
    Object.keys(profile.individual_content ?? {}).length +
    Object.keys(profile.negotiation_positions ?? {}).length

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left bg-white border border-slate-100 rounded-lg p-3 shadow-sm',
        'hover:border-slate-200 hover:shadow-md transition-all group'
      )}
    >
      <div className='flex items-center justify-between mb-2'>
        <div className='flex items-center gap-1.5'>
          {profileApproved ? (
            <CheckCircle2 className='h-3.5 w-3.5 text-emerald-500 flex-shrink-0' />
          ) : (
            <Clock className='h-3.5 w-3.5 text-amber-500 flex-shrink-0' />
          )}
          <span className='text-xs font-semibold text-slate-700'>Prep Profile</span>
        </div>
        <div className='flex items-center gap-1.5'>
          <Badge
            variant='outline'
            className={cn(
              'text-[10px] px-1.5 py-0 h-4 font-medium',
              profileApproved
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            )}
          >
            {profileApproved ? 'Approved' : 'Draft'}
          </Badge>
          <ChevronRight className='h-3 w-3 text-slate-400 group-hover:text-slate-600 transition-colors' />
        </div>
      </div>

      <div className='flex items-center gap-2'>
        <span className='text-[10px] text-slate-400'>{sectionCount} sections</span>
        <span className='text-[10px] text-slate-300'>·</span>
        <Badge
          variant='outline'
          className={cn(
            'text-[10px] px-1.5 py-0 h-4 font-medium',
            SHARING_MODE_COLORS[sharingMode] ?? SHARING_MODE_COLORS.auto
          )}
        >
          {SHARING_MODE_LABELS[sharingMode] ?? sharingMode}
        </Badge>
      </div>

      {profile.shareable_summary && (
        <p className='mt-1.5 text-[10px] text-slate-500 line-clamp-2 leading-relaxed'>
          {profile.shareable_summary}
        </p>
      )}
    </button>
  )
}
